import { useQuery } from "@apollo/client";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import {
  GetScoutConversationContextDocumentsForStaffQuery,
  GetScoutConversationContextPartsForStaffQuery,
  GetScoutConversationContextPreludeForStaffQuery,
  GetScoutConversationContextRoundtripsForStaffQuery,
  GetScoutConversationContextSummaryForStaffQuery,
} from "generated-graphql/graphql";

import { SHOW_ALL_PAGE_SIZE } from "./scoutContextAnalysisFormat";
import {
  GET_SCOUT_CONVERSATION_CONTEXT_DOCUMENTS_FOR_STAFF,
  GET_SCOUT_CONVERSATION_CONTEXT_PARTS_FOR_STAFF,
  GET_SCOUT_CONVERSATION_CONTEXT_PRELUDE_FOR_STAFF,
  GET_SCOUT_CONVERSATION_CONTEXT_ROUNDTRIPS_FOR_STAFF,
  GET_SCOUT_CONVERSATION_CONTEXT_SUMMARY_FOR_STAFF,
} from "./scoutContextAnalysisQueries";

export type SubTab = "parts" | "roundtrips" | "turns" | "documents" | "system" | "tools";

export type ContextSummary = NonNullable<
  GetScoutConversationContextSummaryForStaffQuery["getScoutConversationContextSummaryForStaff"]
>;
export type PartRow = NonNullable<
  GetScoutConversationContextPartsForStaffQuery["getScoutConversationContextPartsForStaff"]
>["items"][number];
export type TurnRow = NonNullable<
  GetScoutConversationContextRoundtripsForStaffQuery["getScoutConversationContextRoundtripsForStaff"]
>["turns"][number];
export type DocumentAttachmentRow = NonNullable<
  GetScoutConversationContextDocumentsForStaffQuery["getScoutConversationContextDocumentsForStaff"]
>["items"][number];
export type PreludeRow = NonNullable<
  GetScoutConversationContextPreludeForStaffQuery["getScoutConversationContextPreludeForStaff"]
>["prelude"];

const FETCH_POLICY = "cache-and-network" as const;

function firstDefined<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return undefined;
}

function skipUnlessTab(subTab: SubTab, ...tabs: SubTab[]): boolean {
  return !tabs.includes(subTab);
}

export function useScoutConversationContextAnalysis({
  conversationId,
  subTab,
  paginationModel,
  documentPaginationModel,
}: {
  conversationId: string;
  subTab: SubTab;
  paginationModel: GridPaginationModel;
  documentPaginationModel: GridPaginationModel;
}) {
  const showAll = paginationModel.pageSize === SHOW_ALL_PAGE_SIZE;
  const summaryQuery = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_SUMMARY_FOR_STAFF, {
    variables: { conversationId },
    fetchPolicy: FETCH_POLICY,
  });
  const roundtripsQuery = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_ROUNDTRIPS_FOR_STAFF, {
    variables: { conversationId },
    fetchPolicy: FETCH_POLICY,
    skip: skipUnlessTab(subTab, "roundtrips", "turns"),
  });
  const partsQuery = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_PARTS_FOR_STAFF, {
    variables: {
      conversationId,
      page: showAll ? null : paginationModel.page,
      pageSize: showAll ? null : paginationModel.pageSize,
      partType: null,
    },
    fetchPolicy: FETCH_POLICY,
    skip: skipUnlessTab(subTab, "parts"),
  });
  const documentsQuery = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_DOCUMENTS_FOR_STAFF, {
    variables: {
      conversationId,
      page: documentPaginationModel.page,
      pageSize: documentPaginationModel.pageSize,
    },
    fetchPolicy: FETCH_POLICY,
    skip: skipUnlessTab(subTab, "documents"),
  });
  const preludeQuery = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_PRELUDE_FOR_STAFF, {
    variables: { conversationId },
    fetchPolicy: FETCH_POLICY,
    skip: skipUnlessTab(subTab, "system", "tools"),
  });

  const summary = summaryQuery.data?.getScoutConversationContextSummaryForStaff;
  const partsResult = partsQuery.data?.getScoutConversationContextPartsForStaff;
  const documentsResult = documentsQuery.data?.getScoutConversationContextDocumentsForStaff;

  return {
    summary,
    turns: roundtripsQuery.data?.getScoutConversationContextRoundtripsForStaff?.turns ?? [],
    partsResult,
    documentsResult,
    prelude: preludeQuery.data?.getScoutConversationContextPreludeForStaff?.prelude,
    error: firstDefined(
      summaryQuery.error,
      roundtripsQuery.error,
      partsQuery.error,
      documentsQuery.error,
      preludeQuery.error
    ),
    partsLoading: partsQuery.loading && !partsResult,
    documentsLoading: documentsQuery.loading && !documentsResult,
    stableRowCount: partsResult?.count ?? summary?.partCount ?? 0,
    stableDocumentRowCount: documentsResult?.count ?? summary?.documentAttachmentCount ?? 0,
  };
}
